import React, {
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
  createRef,
} from "react";
import { Redirect, useParams } from "react-router-dom";

import { useSelector, useDispatch } from "react-redux";
import { usePageWrapper } from "hooks/usePageWrapper";
import { SEOHelmet } from "components/SEO/SEOHelmet";

import * as fromProjects from "store/Projects";
import * as fromInPageNavigation from "store/InPageNavigation";

import WorkDetailsHero from "components/Heros/WorkDetailsHero/WorkDetailsHero";
import ProjectChallenge from "components/Project/ProjectChallenge/ProjectChallenge";
import { ProjectNavSection } from "components/Project/ProjectNavSection/ProjectNavSection";
import NextProject from "components/Project/NextProject/NextProject";

import FooterCta from "components/Footer/FooterCta/FooterCta";

const WorkDetails: React.FC<{}> = () => {
  const { projectName } = useParams();
  const [
    PageWrapperComponent,
    animatePageContentIn,
    changeLoadingScreenLogomarkBackground,
  ] = usePageWrapper();

  const selectProject = useMemo(fromProjects.makeGetProject, []);

  const project: fromProjects.IProject = useSelector((state) =>
    selectProject(state, projectName)
  );

  const dispatch = useDispatch();

  const updateNavigationArrayAction = useCallback(
    (navigationArray: fromInPageNavigation.INavigation[]) =>
      dispatch(
        fromInPageNavigation.actionCreators.updateNavigationArrayAction(
          navigationArray
        )
      ),
    [dispatch]
  );

  const updateTotalSectionsNumberAction = useCallback(
    (section: number) =>
      dispatch(
        fromInPageNavigation.actionCreators.updateTotalSectionsNumberAction(
          section
        )
      ),
    [dispatch]
  );

  const navSections = useRef(
    project !== null
      ? [...Array(project.projectContent.length)].map(() => createRef())
      : null
  );

  const handleResize = () => {
    if (project != null) {
      const navigationArray: fromInPageNavigation.INavigation[] = [];
      project.projectContent.map(
        (navSection: fromProjects.INavSection, index) => {
          const windowScrollTop =
            window.scrollY ||
            window.pageYOffset ||
            document.body.scrollTop +
              ((document.documentElement &&
                document.documentElement.scrollTop) ||
                0);

          const navSectionElement = navSections.current[index]
            .current as Element;

          const navSectionProps = navSectionElement.getBoundingClientRect();

          const scrollTop: number = windowScrollTop + navSectionProps.top;
          const height: number = navSectionProps.height;

          navigationArray.push({
            title: navSection.title,
            scrollTop,
            height,
          });
        }
      );
      updateNavigationArrayAction(navigationArray);
      updateTotalSectionsNumberAction(navigationArray.length);
    }
  };

  useLayoutEffect(() => {
    if (project != null) {
      if (animatePageContentIn) handleResize();

      // Setup event listeners on initial mount to keep the  dimensions of the slider updated
      window.addEventListener("resize", handleResize, { passive: true });

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [animatePageContentIn, project]);

  useEffect(() => {
    if (project != null) {
      const logomarkBackgroundImage: string =
        "/work/" + project.images.logomarkBackground;
      changeLoadingScreenLogomarkBackground(logomarkBackgroundImage);
    }
  }, [project]);

  const getProjectContentSections = () =>
    project.projectContent.map(
      (navSection: fromProjects.INavSection, index) => {
        return (
          <ProjectNavSection
            key={index}
            animationReady={animatePageContentIn}
            navSection={navSection}
            outsideRef={navSections.current[index]}
          />
        );
      }
    );

  if (project == null) return <Redirect to="/work" />;
  else
    return PageWrapperComponent(
      <div className="workDetailsPage">
        <SEOHelmet
          schema="WebPage"
          title={project.title}
          description={project.description}
          contentType="product"
        />
        <WorkDetailsHero
          project={project}
          animationReady={animatePageContentIn}
        />
        <ProjectChallenge
          details={project.projectChallenge}
          animationReady={animatePageContentIn}
        />
        {getProjectContentSections()}
        <NextProject animationReady={animatePageContentIn} />
        <FooterCta animationReady={animatePageContentIn} />
      </div>
    );
};

export default WorkDetails;
